import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock, MessageSquare, CreditCard, UsersRound, Hash, Loader2 } from "lucide-react";
import { useSession } from "@/hooks/useSession";
import { useMyBookings, useConversations, normalizeBooking } from "@/hooks/useApiData";
import { guestApi } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const Dashboard = () => {
  const { user } = useSession();
  const { items: BOOKINGS, loading: bookingsLoading } = useMyBookings();
  const { items: CONVERSATIONS, loading: conversationsLoading } = useConversations();
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const upcoming = BOOKINGS.filter((b) => b.status === "confirmed");
  const past = BOOKINGS.filter((b) => b.status === "completed");
  const next = upcoming[0];
  const unreadTotal = CONVERSATIONS.reduce((n, c) => n + c.unread, 0);
  const loading = bookingsLoading || conversationsLoading;

  const firstName = user?.name?.split(" ")[0] || "there";

  const openBooking = async (booking) => {
    setSelectedBooking(booking);
    setDetailLoading(true);
    try {
      const response = await guestApi.booking(booking.id);
      const value = response?.unwrapped ?? response?.data ?? response;
      setSelectedBooking(normalizeBooking(value) || booking);
    } catch {
      setSelectedBooking(booking);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div data-testid="dashboard-root">
      <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground md:text-xs">
        Account · Overview
      </p>
      <h1 className="mt-3 font-display text-4xl font-medium leading-[0.95] tracking-tight md:mt-4 md:text-6xl">
        Hello, <span className="italic">{firstName}</span>.
      </h1>
      <p className="mt-4 max-w-xl text-sm text-muted-foreground md:mt-5 md:text-base">
        Everything you have on the go with StayEasy, in one place.
      </p>

      {loading ? (
        <DashboardSkeleton />
      ) : (
        <>
          <div className="mt-10 grid grid-cols-2 gap-3 md:mt-14 md:grid-cols-4 md:gap-4">
            <Tile label="Upcoming stays" value={upcoming.length} />
            <Tile label="Past stays" value={past.length} />
            <Tile label="Unread messages" value={unreadTotal} accent={unreadTotal > 0} />
            <Tile label="Reviews to write" value={BOOKINGS.filter((b) => b.canReview).length} />
          </div>

          {next && (
            <section className="mt-12 md:mt-16" data-testid="dashboard-next-stay">
              <div className="flex items-center justify-between gap-4">
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground md:text-xs">Next up</p>
                <button type="button" onClick={() => openBooking(next)} className="text-xs font-medium text-primary hover:underline">View booking details</button>
              </div>
              <div onClick={() => openBooking(next)} role="button" tabIndex={0} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") openBooking(next); }} className="mt-4 grid cursor-pointer grid-cols-1 border border-foreground/10 transition-colors hover:border-primary/40 md:grid-cols-[240px_1fr]">
                <img src={next.property.images[0]} alt="" className="h-48 w-full object-cover md:h-full" />
                <div className="p-6 md:p-8">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{next.property.location}</p>
                  <h2 className="mt-2 font-display text-2xl font-medium tracking-tight md:text-3xl">{next.property.name}</h2>
                  <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5"><Calendar size={13} /> {fmtRange(next.checkIn, next.checkOut)}</span>
                    <span className="inline-flex items-center gap-1.5"><Clock size={13} /> {next.nights} nights · {next.guests} guests</span>
                    <span>Ref {next.reference || next.id}</span>
                  </div>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <Link onClick={(event) => event.stopPropagation()} to={`/account/bookings/${next.id}/messages`} className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-xs font-medium text-background transition-transform hover:-translate-y-0.5">
                      <MessageSquare size={13} /> Message host
                    </Link>
                    <Link onClick={(event) => event.stopPropagation()} to={`/properties/${next.property.id}`} className="inline-flex items-center gap-2 rounded-full border border-foreground/20 px-5 py-2.5 text-xs font-medium transition-colors hover:border-foreground">
                      View property <ArrowRight size={13} />
                    </Link>
                  </div>
                </div>
              </div>
            </section>
          )}

          <section className="mt-14 md:mt-20">
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground md:text-xs">Shortcuts</p>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4">
              <Shortcut to="/account/bookings" label="All bookings" hint="Upcoming & past stays" />
              <Shortcut to="/account/messages" label="Messages" hint={`${unreadTotal} unread`} />
              <Shortcut to="/account/profile" label="Profile settings" hint="Contact & notifications" />
              <Shortcut to="/account/reviews" label="Reviews & testimonials" hint="Share your experience" />
            </div>
          </section>
        </>
      )}

      <BookingDetailDialog booking={selectedBooking} loading={detailLoading} onOpenChange={(open) => { if (!open) setSelectedBooking(null); }} />
    </div>
  );
};

const BookingDetailDialog = ({ booking, loading, onOpenChange }) => (
  <Dialog open={Boolean(booking)} onOpenChange={onOpenChange}>
    <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto p-0">
      {booking && (
        <>
          <div className="border-b border-foreground/10 p-6 md:p-8">
            <DialogHeader>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <DialogTitle className="font-display text-3xl font-medium">Booking details</DialogTitle>
                {loading && <span className="inline-flex items-center gap-2 text-xs text-muted-foreground"><Loader2 size={13} className="animate-spin" /> Refreshing</span>}
              </div>
            </DialogHeader>
            <p className="mt-2 text-sm text-muted-foreground">{booking.property?.name}</p>
          </div>

          <div className="grid gap-0 md:grid-cols-2">
            <DetailSection title="Reservation" icon={Calendar}>
              <Detail label="Booking ID" value={booking.reference || booking.id} mono />
              <Detail label="Status" value={booking.rawStatus || booking.status} capitalize />
              <Detail label="Booked at" value={formatDateTime(booking.created_at)} />
              <Detail label="Check-in" value={formatFullDate(booking.checkIn)} />
              <Detail label="Check-out" value={formatFullDate(booking.checkOut)} />
              <Detail label="Stay" value={`${booking.nights || 0} nights · ${booking.guests || 1} guests`} />
            </DetailSection>

            <DetailSection title="Guests" icon={UsersRound}>
              <Detail label="Primary guest" value={booking.guest?.name || "—"} />
              <Detail label="Email" value={booking.guest?.email || "—"} />
              <Detail label="Phone" value={booking.guest?.phone || "—"} />
              <Detail label="Additional guests" value={(booking.guest?.additional_names || []).join(", ") || "None listed"} />
            </DetailSection>

            <DetailSection title="Property" icon={Hash}>
              <Detail label="Property" value={booking.property?.name || "—"} />
              <Detail label="Location" value={booking.property?.location || "—"} />
              <Detail label="Host" value={booking.property?.host?.name || "—"} />
            </DetailSection>

            <DetailSection title="Payment & references" icon={CreditCard}>
              <Detail label="Total" value={money(booking.total, booking.currency)} />
              <Detail label="Transaction ID" value={booking.transaction?.transaction_id || "—"} mono />
              <Detail label="Invoice" value={booking.transaction?.invoice_number || "—"} mono />
              <Detail label="Stripe Payment Intent" value={booking.stripe_payment_intent_id || "—"} mono />
              <Detail label="Stripe Checkout Session" value={booking.stripe_checkout_session_id || "—"} mono />
              <Detail label="Paid at" value={formatDateTime(booking.paid_at || booking.transaction?.paid_at)} />
            </DetailSection>
          </div>

          <div className="flex flex-wrap gap-3 border-t border-foreground/10 p-6 md:p-8">
            <Link to={`/account/bookings/${booking.id}/messages`} className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-xs font-medium text-background"><MessageSquare size={13} /> Message host</Link>
            <Link to={`/properties/${booking.property?.id}`} className="inline-flex items-center gap-2 rounded-full border border-foreground/20 px-5 py-2.5 text-xs font-medium">View property <ArrowRight size={13} /></Link>
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);

const DetailSection = ({ title, icon: Icon, children }) => (
  <section className="border-b border-foreground/10 p-6 md:border-r md:p-8">
    <h3 className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground"><Icon size={14} /> {title}</h3>
    <div className="mt-5 space-y-4">{children}</div>
  </section>
);

const Detail = ({ label, value, mono, capitalize }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</p>
    <p className={`mt-1 break-words text-sm ${mono ? "font-mono text-xs" : "font-medium"} ${capitalize ? "capitalize" : ""}`}>{value || "—"}</p>
  </div>
);

const DashboardSkeleton = () => (
  <div className="mt-10 animate-pulse md:mt-14">
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">{Array.from({ length: 4 }).map((_, index) => <div key={index} className="h-28 bg-muted" />)}</div>
    <div className="mt-12 h-64 bg-muted md:mt-16" />
    <div className="mt-14 grid gap-3 sm:grid-cols-2 md:mt-20">{Array.from({ length: 4 }).map((_, index) => <div key={index} className="h-24 bg-muted" />)}</div>
  </div>
);

const Tile = ({ label, value, accent }) => (
  <div className={`border p-5 md:p-6 ${accent ? "border-primary bg-primary/5" : "border-foreground/10 bg-card"}`}>
    <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
    <p className={`mt-3 font-display text-4xl font-medium tracking-tight md:text-5xl ${accent ? "text-primary" : "text-foreground"}`}>{value}</p>
  </div>
);

const Shortcut = ({ to, label, hint }) => (
  <Link to={to} className="group flex items-center justify-between border border-foreground/10 bg-card p-5 transition-colors hover:border-foreground/40 md:p-6">
    <div><p className="font-display text-lg font-medium tracking-tight md:text-xl">{label}</p><p className="mt-1 text-xs text-muted-foreground">{hint}</p></div>
    <ArrowRight size={16} className="text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-foreground" />
  </Link>
);

const fmtRange = (a, b) => {
  const fmt = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return a && b ? `${fmt(a)} – ${fmt(b)}` : "Dates unavailable";
};

const formatFullDate = (value) => value ? new Date(`${value}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "—";
const formatDateTime = (value) => value ? new Date(value).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
const money = (value, currency = "USD") => new Intl.NumberFormat("en-US", { style: "currency", currency }).format(Number(value || 0));

export default Dashboard;
